import { StandardEvents, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * ONÉRO — bundle da PDP.
 * Cada linha é uma camisa (cor + tamanho). Linhas que a pessoa não mexeu seguem o seletor
 * principal. O envio do form principal é trocado por um único add com todas as camisas.
 */
class OneroBundle extends HTMLElement {
  #abort = new AbortController();
  #busy = false;

  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-obd-variants]')?.textContent || '[]');
    this.thumbs = JSON.parse(this.querySelector('[data-obd-thumbs]')?.textContent || '{}');
    this.colorPos = Number(this.dataset.colorPos);
    this.sizePos = Number(this.dataset.sizePos);
    this.errorEl = this.querySelector('[data-obd-error]');

    const { signal } = this.#abort;
    this.addEventListener('change', this.#onChange, { signal });
    this.querySelector('[data-obd-upsell]')?.addEventListener('click', () => this.selectTier('2'), { signal });
    this.#bindGift(signal);

    const section = this.closest('.shopify-section');
    section?.addEventListener('change', this.#onMainChange, { signal });
    section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    // window + captura: roda antes do ouvinte do tema, que fica no document (assets/component.js)
    window.addEventListener('submit', this.#onSubmit, { capture: true, signal });

    this.#syncFromMain();
    this.querySelectorAll('[data-row]').forEach((row) => this.#refreshRow(row));
    this.#emit();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  get #section() {
    return this.closest('.shopify-section');
  }

  get #picker() {
    return this.#section?.querySelector('.product-details variant-picker');
  }

  get #productForm() {
    return this.#section?.querySelector('.product-details product-form-component');
  }

  get #selectedTier() {
    return this.querySelector('.obd__tier[data-selected="true"]');
  }

  /* ---------- oferta ---------- */

  selectTier(value) {
    const radio = this.querySelector(`.obd__radio[value="${value}"]`);
    if (radio instanceof HTMLInputElement) radio.checked = true;
    this.querySelectorAll('.obd__tier').forEach((tier) => {
      tier.setAttribute('data-selected', String(tier.getAttribute('data-tier') === value));
    });
    this.#hideError();
    this.#emit();
  }

  /* ---------- brinde da oferta 2 ---------- */

  /**
   * "?": balão flutuante acima da caixa do brinde. No computador abre ao passar o mouse;
   * no toque (e no clique) abre e fecha. Foto: abre maior numa janela.
   */
  #bindGift(signal) {
    const help = this.querySelector('[data-obd-gift-help]');
    const tip = help && this.querySelector(`#${help.getAttribute('aria-controls')}`);
    if (help instanceof HTMLElement && tip instanceof HTMLElement) {
      let pinned = false;
      const show = (open) => {
        tip.hidden = !open;
        if (open) this.#placeTip(help, tip);
        help.setAttribute('aria-expanded', String(open));
      };
      help.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && show(true), { signal });
      help.addEventListener('pointerleave', (e) => e.pointerType === 'mouse' && !pinned && show(false), { signal });
      help.addEventListener('focus', () => show(true), { signal });
      help.addEventListener('blur', () => { pinned = false; show(false); }, { signal });
      help.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          pinned = !pinned || tip.hidden;
          show(pinned);
        },
        { signal }
      );
      document.addEventListener(
        'keydown',
        (e) => {
          if (e.key === 'Escape' && !tip.hidden) {
            pinned = false;
            show(false);
          }
        },
        { signal }
      );
    }

    const zoomButton = this.querySelector('[data-obd-gift-zoom]');
    const zoom = this.querySelector('.obd__zoom');
    if (zoomButton && zoom instanceof HTMLDialogElement) {
      zoomButton.addEventListener('click', () => zoom.showModal(), { signal });
      zoom.addEventListener(
        'click',
        (e) => {
          // fecha no X ou clicando fora da foto
          if (e.target === zoom || (e.target instanceof Element && e.target.closest('[data-obd-zoom-close]'))) zoom.close();
        },
        { signal }
      );
    }
  }

  /** Balão do tamanho da caixa (até 280px), sem sair dela; a seta fica embaixo do "?". */
  #placeTip(help, tip) {
    const box = tip.offsetParent;
    if (!(box instanceof HTMLElement)) return;
    const boxRect = box.getBoundingClientRect();
    const helpRect = help.getBoundingClientRect();
    const center = helpRect.left + helpRect.width / 2 - boxRect.left;
    const width = Math.min(280, boxRect.width);
    const left = Math.max(0, Math.min(center - 24, boxRect.width - width));
    tip.style.setProperty('--tip-left', `${left}px`);
    tip.style.setProperty('--tip-arrow', `${center - left}px`);
  }

  #onChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.classList.contains('obd__radio')) {
      this.selectTier(target.value);
      return;
    }
    if (target instanceof HTMLSelectElement) {
      const row = target.closest('[data-row]');
      if (!row) return;
      row.setAttribute('data-touched', 'true');
      this.#refreshRow(row);
      this.#hideError();
      this.#emit();
    }
  };

  /* ---------- usado pela caixinha de compra (onero-quickbuy) ---------- */

  /** Ofertas e camisas da oferta escolhida, do jeito que estão na tela. */
  bundleState() {
    const tiers = [...this.querySelectorAll('.obd__tier')].map((tier) => ({
      value: tier.getAttribute('data-tier') || '',
      selected: tier.getAttribute('data-selected') === 'true',
      title: tier.querySelector('.obd__title')?.textContent?.trim() || '',
      price: tier.querySelector('.obd__price')?.textContent?.trim() || '',
      compare: tier.querySelector('.obd__compare')?.lastChild?.textContent?.trim() || '',
      badge: tier.querySelector('.obd__badge')?.textContent?.trim() || '',
      // 26/10: preço por camisa em destaque ("£32.50 per shirt"), vazio se o selo estiver desligado
      each: tier.querySelector('.obd__each')?.textContent?.trim() || '',
    }));

    const readSelect = (select) =>
      select instanceof HTMLSelectElement
        ? {
            value: select.value,
            label: select.getAttribute('aria-label') || '',
            options: [...select.options].map((option) => ({ value: option.value, disabled: option.disabled })),
          }
        : null;

    const rows = [...(this.#selectedTier?.querySelectorAll('[data-row]') || [])].map((row) => {
      const num = row.querySelector('.obd__num');
      return {
        label: num?.textContent?.trim() || '',
        free: Boolean(num?.classList.contains('obd__num--free')),
        thumb: row.querySelector('[data-obd-thumb]')?.getAttribute('src') || '',
        color: readSelect(row.querySelector('[data-role="color"]')),
        size: readSelect(row.querySelector('[data-role="size"]')),
      };
    });

    return { tiers, rows };
  }

  /** Muda a cor ou o tamanho de uma camisa da oferta escolhida, como se fosse na tela. */
  setRowValue(index, role, value) {
    const row = this.#selectedTier?.querySelectorAll('[data-row]')[index];
    const select = row?.querySelector(`[data-role="${role}"]`);
    if (!(select instanceof HTMLSelectElement)) return;
    select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #emit() {
    this.dispatchEvent(new CustomEvent('onero-bundle:update', { bubbles: true }));
  }

  /* ---------- linhas seguem o seletor principal ---------- */

  #onMainChange = (event) => {
    if (!(event.target instanceof Element) || this.contains(event.target)) return;
    if (!this.#picker?.contains(event.target)) return;
    this.#syncFromMain();
  };

  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || !this.#picker?.contains(event.target)) return;
    event.promise?.then(() => this.#syncFromMain()).catch(() => {});
  };

  #mainSelection() {
    const picked = { color: null, size: null };
    this.#picker?.querySelectorAll('input:checked, select').forEach((input) => {
      if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return;
      const name = (input.dataset.optionName || input.closest('fieldset')?.dataset.optionName || '').toLowerCase();
      const value = input.value;
      if (/colou?r|cor/.test(name)) picked.color = value;
      else if (/size|tamanho/.test(name)) picked.size = value;
    });
    return picked;
  }

  #syncFromMain() {
    const { color, size } = this.#mainSelection();
    this.querySelectorAll('[data-row]:not([data-touched="true"])').forEach((row) => {
      this.#setSelect(row.querySelector('[data-role="color"]'), color);
      this.#setSelect(row.querySelector('[data-role="size"]'), size);
      this.#refreshRow(row);
    });
    this.#emit();
  }

  #setSelect(select, value) {
    if (!(select instanceof HTMLSelectElement) || value == null) return;
    if ([...select.options].some((option) => option.value === value)) select.value = value;
  }

  /** Foto da cor e tamanhos esgotados daquela cor. */
  #refreshRow(row) {
    const colorSelect = row.querySelector('[data-role="color"]');
    const sizeSelect = row.querySelector('[data-role="size"]');
    const color = colorSelect instanceof HTMLSelectElement ? colorSelect.value : '';

    const img = row.querySelector('[data-obd-thumb]');
    const src = this.thumbs[color];
    if (img instanceof HTMLImageElement && src && img.getAttribute('src') !== src) {
      img.src = src;
      img.alt = color;
    }

    if (!(sizeSelect instanceof HTMLSelectElement)) return;
    for (const option of sizeSelect.options) {
      const variant = this.#findVariant(color, option.value);
      option.disabled = !variant?.a;
    }
    if (sizeSelect.selectedOptions[0]?.disabled) {
      const firstOk = [...sizeSelect.options].find((option) => !option.disabled);
      if (firstOk) sizeSelect.value = firstOk.value;
    }
    sizeSelect.removeAttribute('data-invalid');
  }

  #findVariant(color, size) {
    return this.variants.find(
      (variant) => variant.o[this.colorPos] === color && (this.sizePos < 0 || variant.o[this.sizePos] === size)
    );
  }

  /* ---------- carrinho ---------- */

  /* 22/10 (Kauã): cada camisa entra numa linha própria do carrinho (£34.95 cada no kit de 2).
     A propriedade escondida `_unit` (começa com _, não aparece pro cliente nem no checkout)
     é única por camisa, então a Shopify não junta duas camisas iguais na mesma linha,
     nem com as de um kit adicionado antes. */
  #collectLines() {
    const tier = this.#selectedTier;
    if (!tier) return null;
    const lines = [];
    const batch = Date.now().toString(36);
    let ok = true;

    tier.querySelectorAll('[data-row]').forEach((row) => {
      const color = row.querySelector('[data-role="color"]')?.value ?? '';
      const sizeSelect = row.querySelector('[data-role="size"]');
      const size = sizeSelect?.value ?? '';
      const variant = this.#findVariant(color, size);
      if (!variant?.a) {
        ok = false;
        sizeSelect?.setAttribute('data-invalid', 'true');
        return;
      }
      lines.push({ id: variant.id, quantity: 1, properties: { _unit: `${batch}-${lines.length + 1}` } });
    });

    if (!ok) return null;

    // 23/10: brinde da oferta 2 (Envoy Collar Stays, £0). `_onero_kit_gift` deixa o carrinho
    // saber que é brinde do kit e tirar sozinho se ficar com menos de 4 Envoys.
    // Vai na frente da lista: a Shopify mostra o último item adicionado em cima, então o brinde
    // fica embaixo das camisas.
    const giftId = Number(tier.querySelector('[data-obd-gift]')?.getAttribute('data-obd-gift'));
    if (giftId) lines.unshift({ id: giftId, quantity: 1, properties: { _onero_kit_gift: batch } });

    return lines;
  }

  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const productForm = this.#productForm;
    if (!productForm || form.closest('product-form-component') !== productForm) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#add(productForm);
  };

  async #add(productForm) {
    if (this.#busy) return;
    const lines = this.#collectLines();
    if (!lines) {
      this.#showError('Pick an available size for every shirt.');
      return;
    }

    this.#busy = true;
    this.#hideError();

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);
    const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();

    productForm.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
        promise: deferred.promise,
      })
    );

    const detailBase = {
      source: 'product-form-component',
      sourceId: String(productForm.id || ''),
      itemCount,
      productId: productForm.dataset.productId,
    };

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lines, sections: sectionIds.join(',') }),
      }).then((res) => res.json());

      const cart = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((res) => res.json());

      if (response.status) {
        productForm.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detailBase, items: cart.items, didError: true },
        });
        this.#showError(response.description || response.message || 'Could not add to cart.');
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { ...detailBase, items: cart.items, sections: response.sections, didError: false },
      });
    } catch (error) {
      deferred.reject(error);
      productForm.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#showError('Could not add to cart. Try again.');
    } finally {
      this.#busy = false;
    }
  }

  #showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  #hideError() {
    if (this.errorEl) this.errorEl.hidden = true;
  }
}

if (!customElements.get('onero-bundle')) {
  customElements.define('onero-bundle', OneroBundle);
}
